export interface RatedLibraryPlace {
  library_item_id: string;
  host_id: string;
  host_name: string | null;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  avg_rating: number;
  rating_count: number;
}

export interface PlaceGroup {
  /** Display name: that of the most-rated entry. */
  name: string;
  address: string | null;
  /** Average over every rating of every entry in the group. */
  avg: number;
  count: number;
  entries: RatedLibraryPlace[];
}

/** Two entries closer than this (metres) are the same spot. */
const SAME_SPOT_METRES = 100;

function normalize(s: string | null | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function metresBetween(a: RatedLibraryPlace, b: RatedLibraryPlace): number {
  const rad = Math.PI / 180;
  const dLat = ((b.latitude as number) - (a.latitude as number)) * rad;
  const dLng = ((b.longitude as number) - (a.longitude as number)) * rad;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos((a.latitude as number) * rad) * Math.cos((b.latitude as number) * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
}

function hasCoords(p: RatedLibraryPlace): boolean {
  return p.latitude != null && p.longitude != null;
}

/**
 * The same place registered by several hosts: same name (ignoring case, accents
 * and punctuation) and within ~100 m of each other. Without coordinates on both
 * sides the same address stands in for the distance. Best-effort, computed on
 * the fly: nothing is stored, so a wrong merge never sticks.
 */
export function isSamePlace(a: RatedLibraryPlace, b: RatedLibraryPlace): boolean {
  const name = normalize(a.name);
  if (!name || name !== normalize(b.name)) return false;
  if (hasCoords(a) && hasCoords(b)) return metresBetween(a, b) <= SAME_SPOT_METRES;
  const addr = normalize(a.address);
  return addr !== '' && addr === normalize(b.address);
}

/** Groups entries that are the same place; best-rated-count groups first, then by name. */
export function groupPlaces(places: RatedLibraryPlace[]): PlaceGroup[] {
  const parent = places.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  for (let i = 0; i < places.length; i++) {
    for (let j = i + 1; j < places.length; j++) {
      if (isSamePlace(places[i], places[j])) parent[find(j)] = find(i);
    }
  }

  const buckets = new Map<number, RatedLibraryPlace[]>();
  places.forEach((p, i) => {
    const root = find(i);
    buckets.set(root, [...(buckets.get(root) ?? []), p]);
  });

  return [...buckets.values()]
    .map((entries) => {
      const lead = [...entries].sort((a, b) => b.rating_count - a.rating_count)[0];
      const count = entries.reduce((n, e) => n + Number(e.rating_count), 0);
      const sum = entries.reduce((n, e) => n + Number(e.avg_rating) * Number(e.rating_count), 0);
      return {
        name: lead.name,
        address: entries.find((e) => e.address)?.address ?? null,
        avg: Math.round((sum / count) * 10) / 10,
        count,
        entries,
      };
    })
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
